import { useState, useEffect, useCallback } from "react";
import { C, S } from "../customers/theme";
import { getMedia, uploadMedia, addVideo, setPrimary, deleteMedia, reorderMedia } from "../lib/productMedia";
import { getAttributes, createAttribute, deleteAttribute, addOption, deleteOption, getProductValues, setProductValue, MAX_ATTRIBUTES } from "../lib/productAttributes";
import { getLinks, addLink, removeLink, LINK_TYPES } from "../lib/productLinks";
import { getProducts } from "../lib/products";
import { listUnits, createUnit, updateUnit, deleteUnit } from "../lib/productUnits";
import ProductPicker from "./ProductPicker";
import { resizeImage } from "./imageResize";

const TABS = ["Media", "Attributes", "Related", "Units"];
const FACTOR_HINT = "how many base units are in one of this pack (each = 1, e.g. case = 24)";
const ErrorBar = ({ error, onDismiss }) => error ? (
  <div role="alert" style={{ display: "flex", alignItems: "center", gap: 10, background: C.dangerDim, border: `1px solid ${C.danger}`, color: C.text, borderRadius: 8, padding: "8px 12px", margin: "8px 0", fontSize: 13 }}>
    <span style={{ color: C.danger, fontWeight: 700 }}>⚠ {error}</span>
    <button style={S.btn("secondary")} onClick={onDismiss}>Dismiss</button>
  </div>
) : null;

// ── Media ─────────────────────────────────────────────────────────────────────
function MediaTab({ productId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [videoUrl, setVideoUrl] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows(await getMedia(productId)); } catch (e) { setError(e.message); } finally { setLoading(false); }
  }, [productId]);
  useEffect(() => { load(); }, [load]);

  const onFiles = async (e) => {
    const files = [...(e.target.files || [])];
    e.target.value = "";
    if (!files.length) return;
    setBusy(true); setError("");
    try { for (const file of files) { const f = await resizeImage(file); await uploadMedia(productId, f, { title: file.name }); } await load(); }
    catch (err) { setError(err.message); } finally { setBusy(false); }
  };
  const onAddVideo = async () => {
    setBusy(true); setError("");
    try { await addVideo(productId, videoUrl); setVideoUrl(""); await load(); }
    catch (err) { setError(err.message); } finally { setBusy(false); }
  };
  const act = async (fn) => { setBusy(true); setError(""); try { await fn(); await load(); } catch (e) { setError(e.message); } finally { setBusy(false); } };
  const move = (i, dir) => {
    const j = i + dir; if (j < 0 || j >= rows.length) return;
    const ids = rows.map((r) => r.id); [ids[i], ids[j]] = [ids[j], ids[i]];
    act(() => reorderMedia(ids));
  };

  return (
    <div>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 8 }}>
        <label style={{ ...S.btn(), cursor: busy ? "default" : "pointer", opacity: busy ? 0.6 : 1 }}>
          {busy ? "Uploading…" : "+ Upload photo / PDF"}
          <input type="file" accept="image/*,application/pdf" multiple onChange={onFiles} disabled={busy} style={{ display: "none" }} aria-label="Upload photo or PDF" />
        </label>
        <div style={{ display: "flex", gap: 6, alignItems: "center", flex: 1, minWidth: 240 }}>
          <input style={S.input} placeholder="Video embed URL (YouTube/Vimeo)…" value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)} aria-label="Video URL" />
          <button style={S.btn("secondary")} onClick={onAddVideo} disabled={busy || !videoUrl.trim()}>Add video</button>
        </div>
      </div>
      <ErrorBar error={error} onDismiss={() => setError("")} />
      {loading ? <div style={S.empty}>Loading…</div> : rows.length === 0 ? (
        <div style={{ ...S.card, textAlign: "center", padding: 24, color: C.textMuted }}>No media yet. Upload photos/PDFs or add a video URL.</div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 12 }}>
          {rows.map((m, i) => (
            <div key={m.id} style={{ ...S.card, padding: 8, marginBottom: 0 }}>
              <div style={{ height: 96, borderRadius: 6, background: C.surface2, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", marginBottom: 6 }}>
                {m.kind === "photo" && m.url ? <img loading="lazy" src={m.url} alt={m.title || "product photo"} style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "cover" }} />
                  : <span style={{ fontSize: 30 }}>{m.kind === "pdf" ? "📄" : m.kind === "video" ? "▶️" : "🖼"}</span>}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 4 }}>
                {m.is_primary && <span style={S.pill(C.success)}>Primary</span>}
                <span style={{ fontSize: 11, color: C.textMuted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{m.title || m.kind}</span>
              </div>
              <div style={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                {!m.is_primary && <button style={{ ...S.btn("ghost"), padding: "3px 6px" }} title="Set primary" aria-label={`Set ${m.title || m.kind} primary`} onClick={() => act(() => setPrimary(m.id))} disabled={busy}>★</button>}
                <button style={{ ...S.btn("ghost"), padding: "3px 6px" }} aria-label="Move up" onClick={() => move(i, -1)} disabled={busy || i === 0}>↑</button>
                <button style={{ ...S.btn("ghost"), padding: "3px 6px" }} aria-label="Move down" onClick={() => move(i, 1)} disabled={busy || i === rows.length - 1}>↓</button>
                <button style={{ ...S.btn("ghost"), padding: "3px 6px" }} title="Delete" aria-label={`Delete ${m.title || m.kind}`} onClick={() => { if (window.confirm("Delete this media?")) act(() => deleteMedia(m.id)); }} disabled={busy}>🗑</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Attributes (per-product values + a definitions manager) ────────────────────
function DefinitionsManager({ attributes, onChanged }) {
  const [name, setName] = useState("");
  const [level, setLevel] = useState(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [optionText, setOptionText] = useState({});
  const act = async (fn) => { setBusy(true); setError(""); try { await fn(); await onChanged(); } catch (e) { setError(e.message); } finally { setBusy(false); } };

  return (
    <div style={{ ...S.card, marginTop: 10 }}>
      <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>Manage dimensions ({attributes.length}/{MAX_ATTRIBUTES})</div>
      <ErrorBar error={error} onDismiss={() => setError("")} />
      {attributes.map((a) => (
        <div key={a.id} style={{ borderTop: `1px solid ${C.borderSoft}`, padding: "8px 0" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
            <strong style={{ fontSize: 13 }}>{a.name}</strong>
            <span style={{ color: C.textDim, fontSize: 11 }}>level {a.level}</span>
            <div style={{ flex: 1 }} />
            <button style={{ ...S.btn("ghost"), padding: "3px 8px" }} aria-label={`Delete ${a.name}`} onClick={() => { if (window.confirm(`Delete dimension "${a.name}" and its values?`)) act(() => deleteAttribute(a.id)); }} disabled={busy}>🗑</button>
          </div>
          <div style={{ display: "flex", gap: 5, flexWrap: "wrap", alignItems: "center" }}>
            {(a.product_attribute_options || []).map((o) => (
              <span key={o.id} style={{ ...S.pill(C.surface2), color: C.text, display: "inline-flex", gap: 6, alignItems: "center" }}>
                {o.value}
                <button style={{ background: "none", border: "none", color: C.textMuted, cursor: "pointer", padding: 0 }} aria-label={`Remove option ${o.value}`} onClick={() => act(() => deleteOption(o.id))} disabled={busy}>✕</button>
              </span>
            ))}
            <input style={{ ...S.input, width: 120 }} placeholder="+ option" value={optionText[a.id] || ""} onChange={(e) => setOptionText((p) => ({ ...p, [a.id]: e.target.value }))}
              onKeyDown={(e) => { if (e.key === "Enter" && (optionText[a.id] || "").trim()) { const v = optionText[a.id].trim(); setOptionText((p) => ({ ...p, [a.id]: "" })); act(() => addOption(a.id, v, (a.product_attribute_options || []).length)); } }}
              aria-label={`Add option to ${a.name}`} />
          </div>
        </div>
      ))}
      {attributes.length < MAX_ATTRIBUTES && (
        <div style={{ display: "flex", gap: 6, alignItems: "flex-end", marginTop: 10, borderTop: `1px solid ${C.borderSoft}`, paddingTop: 10 }}>
          <div style={{ flex: 1 }}><label style={S.label}>New dimension</label><input style={S.input} placeholder="Colour / Size / Brand…" value={name} onChange={(e) => setName(e.target.value)} aria-label="New dimension name" /></div>
          <div style={{ width: 90 }}><label style={S.label}>Level</label>
            <select style={S.input} value={level} onChange={(e) => setLevel(+e.target.value)} aria-label="Dimension level">{[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}</select>
          </div>
          <button style={S.btn()} disabled={busy || !name.trim()} onClick={() => { const n = name.trim(); setName(""); act(() => createAttribute({ name: n, level, position: attributes.length })); }}>Add</button>
        </div>
      )}
    </div>
  );
}

function AttributesTab({ productId }) {
  const [attrs, setAttrs] = useState([]);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [managing, setManaging] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const [a, v] = await Promise.all([getAttributes(), getProductValues(productId)]); setAttrs(a); setValues(v); }
    catch (e) { setError(e.message); } finally { setLoading(false); }
  }, [productId]);
  useEffect(() => { load(); }, [load]);

  const onValue = async (attributeId, value) => {
    setValues((p) => ({ ...p, [attributeId]: value }));
    try { await setProductValue(productId, attributeId, value); } catch (e) { setError(e.message); }
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
        <div style={{ color: C.textMuted, fontSize: 13 }}>Set this product's filterable attributes.</div>
        <div style={{ flex: 1 }} />
        <button style={S.btn("secondary")} onClick={() => setManaging((m) => !m)}>{managing ? "Done" : "Manage dimensions"}</button>
      </div>
      <ErrorBar error={error} onDismiss={() => setError("")} />
      {loading ? <div style={S.empty}>Loading…</div> : attrs.length === 0 ? (
        <div style={{ ...S.card, textAlign: "center", padding: 20, color: C.textMuted }}>No dimensions yet. Click <strong style={{ color: C.text }}>Manage dimensions</strong> to define up to {MAX_ATTRIBUTES} (e.g. Colour, Size).</div>
      ) : (
        <div style={S.grid2}>
          {attrs.map((a) => {
            const opts = a.product_attribute_options || [];
            return (
              <div key={a.id}>
                <label style={S.label}>{a.name}</label>
                {opts.length ? (
                  <select style={S.input} value={values[a.id] ?? ""} onChange={(e) => onValue(a.id, e.target.value)} aria-label={a.name}>
                    <option value="">—</option>
                    {opts.map((o) => <option key={o.id} value={o.value}>{o.value}</option>)}
                  </select>
                ) : (
                  <input style={S.input} value={values[a.id] ?? ""} onChange={(e) => onValue(a.id, e.target.value)} aria-label={a.name} placeholder="value" />
                )}
              </div>
            );
          })}
        </div>
      )}
      {managing && <DefinitionsManager attributes={attrs} onChanged={load} />}
    </div>
  );
}

// ── Related products ────────────────────────────────────────────────────────────
function RelatedTab({ product }) {
  const [links, setLinks] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [linkType, setLinkType] = useState("related");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [l, p] = await Promise.all([getLinks(product.id), getProducts({ pageSize: 1000, sortBy: "name" })]);
      setLinks(l); setProducts((p.data || []).filter((x) => x.id !== product.id));
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  }, [product.id]);
  useEffect(() => { load(); }, [load]);

  const onPick = async (picked) => {
    setBusy(true); setError("");
    try { await addLink(product.id, picked.id, linkType); await load(); } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  const onRemove = async (id) => { setBusy(true); setError(""); try { await removeLink(id); await load(); } catch (e) { setError(e.message); } finally { setBusy(false); } };

  return (
    <div>
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8, flexWrap: "wrap" }}>
        <select style={{ ...S.input, width: "auto" }} value={linkType} onChange={(e) => setLinkType(e.target.value)} aria-label="Link type">
          {LINK_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <div style={{ flex: 1, minWidth: 220 }}><ProductPicker products={products} onSelect={onPick} style={{ width: "100%" }} /></div>
      </div>
      <ErrorBar error={error} onDismiss={() => setError("")} />
      {loading ? <div style={S.empty}>Loading…</div> : links.length === 0 ? (
        <div style={{ ...S.card, textAlign: "center", padding: 20, color: C.textMuted }}>No linked products. Pick a product above to add a related / similar / upsell link.</div>
      ) : (
        <table style={S.table}>
          <thead><tr><th style={S.th}>Type</th><th style={S.th}>Product</th><th style={S.th} aria-label="Actions"></th></tr></thead>
          <tbody>
            {links.map((l) => (
              <tr key={l.id}>
                <td style={S.td}><span style={S.pill(C.primaryDim)}>{l.link_type}</span></td>
                <td style={S.td}>{l.related?.name || "—"} {l.related?.product_number && <span style={{ color: C.textMuted, fontFamily: "monospace", fontSize: 11 }}>{l.related.product_number}</span>}</td>
                <td style={S.td}><button style={S.btn("ghost")} aria-label={`Remove ${l.related?.name || "link"}`} onClick={() => onRemove(l.id)} disabled={busy}>🗑</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ── Units (pack hierarchy — base + default-sell single-select) ────────────────
function UnitsTab({ productId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [add, setAdd] = useState({ code: "", name: "", factor: "", barcode: "" });
  const [editingId, setEditingId] = useState(null);
  const [edit, setEdit] = useState({ code: "", name: "", factor: "", barcode: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows(await listUnits(productId)); } catch (e) { setError(e.message); } finally { setLoading(false); }
  }, [productId]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn) => { setBusy(true); setError(""); try { await fn(); await load(); } catch (e) { setError(e.message); } finally { setBusy(false); } };

  const onAdd = () => {
    const code = add.code.trim();
    if (!code) return;
    act(async () => { await createUnit(productId, { ...add, code }); setAdd({ code: "", name: "", factor: "", barcode: "" }); });
  };
  const startEdit = (u) => { setEditingId(u.id); setEdit({ code: u.code || "", name: u.name || "", factor: String(Number(u.factor)), barcode: u.barcode || "" }); };
  const saveEdit = (id) => act(async () => { await updateUnit(id, edit); setEditingId(null); });
  const onDelete = (u) => { if (window.confirm(`Delete unit "${u.code}"?`)) act(() => deleteUnit(u.id)); };
  const setBase = (id) => act(() => updateUnit(id, { is_base: true }));
  const setDefault = (id) => act(() => updateUnit(id, { is_default_sell: true }));

  return (
    <div>
      <div style={{ color: C.textMuted, fontSize: 13, marginBottom: 8 }}>
        Pack units for this product. <strong style={{ color: C.text }}>Base</strong> and <strong style={{ color: C.text }}>Default sell</strong> are single-select — exactly one of each.
      </div>
      <ErrorBar error={error} onDismiss={() => setError("")} />
      {loading ? <div style={S.empty}>Loading…</div> : (
        <table style={S.table}>
          <thead><tr>
            <th style={S.th}>Base</th><th style={S.th}>Default sell</th><th style={S.th}>Code</th>
            <th style={S.th}>Name</th><th style={S.th}>Factor</th><th style={S.th}>Barcode</th><th style={S.th} aria-label="Actions"></th>
          </tr></thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td style={S.empty} colSpan={7}>No units yet. Add one below — your first unit becomes the base (each = factor 1).</td></tr>
            ) : rows.map((u) => {
              const editing = editingId === u.id;
              return (
                <tr key={u.id}>
                  <td style={S.td}><input type="radio" name={`base-${productId}`} checked={u.is_base} onChange={() => setBase(u.id)} disabled={busy} aria-label={`Set ${u.code} as the base unit`} /></td>
                  <td style={S.td}><input type="radio" name={`default-${productId}`} checked={u.is_default_sell} onChange={() => setDefault(u.id)} disabled={busy} aria-label={`Set ${u.code} as the default sell unit`} /></td>
                  {editing ? (
                    <>
                      <td style={S.td}><input style={{ ...S.input, width: 90 }} value={edit.code} onChange={(e) => setEdit((p) => ({ ...p, code: e.target.value }))} aria-label="Code" /></td>
                      <td style={S.td}><input style={{ ...S.input, width: 130 }} value={edit.name} onChange={(e) => setEdit((p) => ({ ...p, name: e.target.value }))} aria-label="Name" /></td>
                      <td style={S.td}><input style={{ ...S.input, width: 74 }} type="number" min="0" step="0.0001" value={edit.factor} onChange={(e) => setEdit((p) => ({ ...p, factor: e.target.value }))} aria-label="Factor" /></td>
                      <td style={S.td}><input style={{ ...S.input, width: 130 }} value={edit.barcode} onChange={(e) => setEdit((p) => ({ ...p, barcode: e.target.value }))} aria-label="Barcode" /></td>
                      <td style={S.td}>
                        <button style={{ ...S.btn(), padding: "3px 8px" }} onClick={() => saveEdit(u.id)} disabled={busy || !edit.code.trim()}>Save</button>
                        <button style={{ ...S.btn("ghost"), padding: "3px 8px" }} onClick={() => setEditingId(null)} disabled={busy}>Cancel</button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td style={S.td}>{u.code}{u.is_base && <span style={{ ...S.pill(C.primaryDim), marginLeft: 6 }}>base</span>}</td>
                      <td style={S.td}>{u.name || "—"}</td>
                      <td style={S.td}>{Number(u.factor)}</td>
                      <td style={S.td}>{u.barcode ? <span style={{ fontFamily: "monospace", fontSize: 12 }}>{u.barcode}</span> : "—"}</td>
                      <td style={S.td}>
                        <button style={{ ...S.btn("ghost"), padding: "3px 8px" }} aria-label={`Edit ${u.code}`} onClick={() => startEdit(u)} disabled={busy}>Edit</button>
                        <button style={{ ...S.btn("ghost"), padding: "3px 8px" }} aria-label={`Delete ${u.code}`} onClick={() => onDelete(u)} disabled={busy}>🗑</button>
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {/* Add a unit */}
      <div style={{ ...S.card, marginTop: 10, display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
        <div style={{ width: 100 }}><label style={S.label}>Code</label><input style={S.input} placeholder="case" value={add.code} onChange={(e) => setAdd((p) => ({ ...p, code: e.target.value }))} aria-label="New unit code" /></div>
        <div style={{ width: 150 }}><label style={S.label}>Name</label><input style={S.input} placeholder="Case of 24" value={add.name} onChange={(e) => setAdd((p) => ({ ...p, name: e.target.value }))} aria-label="New unit name" /></div>
        <div style={{ width: 90 }}><label style={S.label}>Factor</label><input style={S.input} type="number" min="0" step="0.0001" placeholder="24" value={add.factor} onChange={(e) => setAdd((p) => ({ ...p, factor: e.target.value }))} aria-label="New unit factor" /></div>
        <div style={{ width: 160 }}><label style={S.label}>Barcode</label><input style={S.input} placeholder="Case barcode" value={add.barcode} onChange={(e) => setAdd((p) => ({ ...p, barcode: e.target.value }))} aria-label="New unit barcode" /></div>
        <button style={S.btn()} disabled={busy || !add.code.trim()} onClick={onAdd}>Add unit</button>
      </div>
      <div style={{ color: C.textDim, fontSize: 11.5, marginTop: 6 }}>Factor = {FACTOR_HINT}.</div>
    </div>
  );
}

export default function ProductCatalogueEditor({ product, onClose }) {
  const [tab, setTab] = useState("Media");
  const tabBtn = (t) => ({
    padding: "7px 13px", borderRadius: 7, border: `1px solid ${tab === t ? C.primary : C.border}`, cursor: "pointer",
    fontWeight: 600, fontSize: 12.5, background: tab === t ? C.primaryDim : "transparent", color: tab === t ? C.primary : C.textMuted,
  });
  return (
    <div style={S.modal} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={{ ...S.modalBox, width: 820 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Catalogue — {product.name}
            {product.product_number && <span style={{ color: C.textMuted, fontWeight: 500, fontSize: 13, marginLeft: 8, fontFamily: "monospace" }}>{product.product_number}</span>}
          </h2>
          <button onClick={onClose} aria-label="Close" style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: C.textMuted }}>✕</button>
        </div>
        <div style={{ display: "flex", gap: 7, marginBottom: 14 }}>
          {TABS.map((t) => <button key={t} style={tabBtn(t)} onClick={() => setTab(t)} aria-pressed={tab === t}>{t}</button>)}
        </div>
        {tab === "Media" && <MediaTab productId={product.id} />}
        {tab === "Attributes" && <AttributesTab productId={product.id} />}
        {tab === "Related" && <RelatedTab product={product} />}
        {tab === "Units" && <UnitsTab productId={product.id} />}
      </div>
    </div>
  );
}
